import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { installerBranding } from "@/branding/Branding";
import BackButton from "@/components/BackButton";

/**
 * Public "Forgot password" screen.
 *
 * The backend always returns the same generic message regardless of
 * whether the account exists, so this page mirrors that: on submit we
 * show the neutral confirmation copy and never disclose account state.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    async function onSubmit(e) {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await api.forgotPassword(email.trim());
            setSent(true);
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Try again.");
        } finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7] px-4">
            <div className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8">
                <img src={installerBranding.logoUrl} alt={installerBranding.name} className="h-10 w-auto mb-6" />
                <h1 className="text-2xl text-[#131B2F] font-medium">Forgot your password?</h1>
                <p className="mt-2 text-sm text-[#4B5565]">
                    Enter your account email and we'll send you a secure link to set a new password.
                </p>

                {sent ? (
                    <div
                        className="mt-6 p-4 rounded-md bg-[#E6F1DE] text-[#2E7E22] text-sm"
                        data-testid="forgot-sent"
                    >
                        If an account exists for this email address, password-reset instructions will be issued.
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="mt-5" data-testid="forgot-form">
                        <label className="block text-sm text-[#4B5565] mb-1.5">Email</label>
                        <input
                            data-testid="forgot-email-input"
                            type="email" required autoComplete="email"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base outline-none focus:border-[#3E9E2E]"
                        />
                        {error && <div className="mt-3 text-sm text-[#DA3535]" data-testid="forgot-error">{error}</div>}
                        <button
                            type="submit" disabled={busy}
                            data-testid="forgot-submit"
                            className="mt-5 w-full h-11 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] disabled:opacity-70 text-white text-base font-medium"
                        >
                            {busy ? "Sending…" : "Send reset link"}
                        </button>
                    </form>
                )}

                <div className="mt-6 flex justify-center">
                    <BackButton fallbackHref="/" label="Back to sign in" testid="forgot-back-to-login" />
                </div>
            </div>
        </div>
    );
}
